import React from 'react';
import { ChevronRight, ListTodo } from 'lucide-react';
import { generateTodoKey } from '@/helpers/utils';
import { useTodos } from '@/context/TodoContext';
import TodoItemDisplay from '@/components/shared/TodoItemDisplay';
import { sortTodosByStatus, calculateProgressPercentage } from '@/utils/todo.utils';
import { SIDEBAR_WIDTH } from '@/constants/ui.constants';

const TodosSidebar: React.FC = () => {
  const {
    todos,
    isTodoPanelOpen,
    setIsTodoPanelOpen,
    completedCount,
    totalCount,
  } = useTodos();

  const handleClose = (): void => {
    setIsTodoPanelOpen(false);
  };

  const sortedTodos = sortTodosByStatus(todos);
  const progressPercentage = calculateProgressPercentage(
    completedCount,
    totalCount
  );

  return (
    <aside
      className={`bg-card dark:bg-card-dark text-card-foreground dark:text-card-foreground-dark border-l border-border flex flex-col transition-all duration-300 ease-in-out relative ${isTodoPanelOpen ? SIDEBAR_WIDTH : 'w-0 overflow-hidden'}`}
    >
      {isTodoPanelOpen && (
        <>
          {/* Header */}
          <div
            className="flex items-center justify-between p-4 border-b border-border"
          >
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="p-1.5 hover:bg-muted dark:hover:bg-selected-item-dark rounded-md transition-colors text-foreground dark:text-foreground-dark"
                title="Close Tasks"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <ListTodo className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-medium text-foreground dark:text-foreground-dark">
                Tasks
              </h3>
            </div>

            {totalCount > 0 && (
              <span className="text-sm text-foreground dark:text-foreground-dark">
                {completedCount}/{totalCount}
              </span>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {totalCount === 0 ? (
              <div
                className="flex-1 flex flex-col items-center justify-center p-4"
              >
                <ListTodo
                  className="size-12 text-foreground/50 dark:text-foreground-dark/50 mb-3"
                />
                <p className="text-sm text-foreground dark:text-foreground-dark text-center">
                  No tasks yet
                </p>
                <p
                  className="text-xs text-foreground/70 dark:text-foreground-dark/70 text-center mt-1"
                >
                  Tasks from the assistant will appear here
                </p>
              </div>
            ) : (
              <>
                {/* Progress bar */}
                <div className="px-4 py-3 border-b border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground dark:text-foreground-dark">
                      Progress
                    </span>
                    <span className="text-sm font-medium text-foreground dark:text-foreground-dark">
                      {completedCount} / {totalCount}
                    </span>
                  </div>
                  <div
                    className="w-full h-2 bg-foreground/10 dark:bg-foreground-dark/10 rounded-full overflow-hidden"
                  >
                    <div
                      className="h-full bg-primary transition-all duration-300 rounded-full"
                      style={{
                        width: `${progressPercentage}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Todos list */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {sortedTodos.map((todo, index) => (
                    <TodoItemDisplay
                      key={generateTodoKey(todo, index)}
                      todo={todo}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );
};

export default TodosSidebar;

